// components/ui/theme-switcher.tsx
"use client";

import { setThemeCookie } from "@/data/cookieClient";
import { useIsArabic } from "@/hooks/use-isArabic";
import { ThemeType } from "@/lib/types";
import { _t, cn } from "@/lib/utils";
import useAppSettings from "@/store/appSettings";
import { MoonIcon, SunIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./button";

const ThemeSwitcher = ({
  label = true,
  classNames,
}: {
  label?: boolean;
  classNames?: string;
}) => {
  const { theme, setTheme } = useAppSettings();
  const { lang } = useIsArabic();

  // Track hydration state
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Hydration is complete
    setIsHydrated(true);

    // Apply the theme on client side after hydration
    document.documentElement.classList.toggle("dark", theme === ThemeType.DARK);
  }, [theme]); // Depend on the theme to apply it

  const toggleTheme = () => {
    const newTheme = theme === ThemeType.DARK ? ThemeType.LIGHT : ThemeType.DARK;
    setTheme(newTheme);
    setThemeCookie(newTheme);
  };

  // Wait for hydration to complete before rendering the theme switcher
  if (!isHydrated) {
    return null; // Don't render until hydration is complete
  }

  return (
    <Button
      onClick={toggleTheme}
      variant="ghost"
      className={cn(
        "flex items-center gap-2 p-2 rounded-sm border max-h-[34px] border-cardBorderLight dark:bg-black dark:hover:bg-buttonPrimaryDark dark:border-inputBorderDark px-3 py-1.5 text-sm",
        classNames && classNames
      )}
      aria-label={_t(
        theme === "dark" ? "switch_to_light_mode" : "switch_to_dark_mode",
        lang
      )}
    >
      <span className="flex gap-x-2 items-center">
        {theme === "dark" ? (
          <>
            {label && <span>{_t("light_mode", lang)}</span>}
            <SunIcon size={20} aria-hidden="true" />
          </>
        ) : (
          <>
            <MoonIcon size={20} aria-hidden="true" />
            {label && <span>{_t("dark_mode", lang)}</span>}
          </>
        )}
      </span>
    </Button>
  );
};

export default ThemeSwitcher;